import {
  donationPaymentMethodSchema,
  REFERENCED_PAYMENT_OUTCOMES,
  REFERENCED_PAYMENT_PROVIDERS,
  STAFF_ENTRY_CURRENCIES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { minorAmountSchema, moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { phoneSchema } from './common/phone.js';
import { donationFormDataSchema, inlinePayerSchema } from './donation.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/** The path a campaign's signed donation endpoint hangs under — docs/decisions/0138. */
export const INTEGRATION_CAMPAIGN_DONATION_PATH =
  '/api/webhooks/integrations/{clientRef}/campaigns/{campaignId}/donations';

const STRIPE_PAYMENT_ID = /^pi_[A-Za-z0-9]{8,120}$/;
const PAYPAL_PAYMENT_ID = /^[A-Za-z0-9_-]{6,64}$/;

/**
 * The payment a donation arrived already paid by (docs/decisions/0141): the
 * provider's own success id, exactly the id the provider's movement is stored
 * under — a Stripe **PaymentIntent** (`pi_…`, never a charge `ch_…`, a Checkout
 * Session `cs_…` or a client secret) and a PayPal **capture / transaction** id.
 */
export const integrationPaymentSchema = z
  .object({
    provider: z.enum(REFERENCED_PAYMENT_PROVIDERS),
    transactionId: z.string().trim().min(1).max(255),
  })
  .strict()
  .superRefine((payment, ctx) => {
    const shape = payment.provider === 'stripe' ? STRIPE_PAYMENT_ID : PAYPAL_PAYMENT_ID;
    if (!shape.test(payment.transactionId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['transactionId'],
        message: 'not the id shape this provider issues',
      });
    }
  });

export type IntegrationPayment = z.infer<typeof integrationPaymentSchema>;

/** What the endpoint answers about the payment a donation named — `null` when it named none. */
export const integrationPaymentMatchSchema = z.enum(REFERENCED_PAYMENT_OUTCOMES);

/**
 * The body of `POST /api/webhooks/integrations/{clientRef}/campaigns/{campaignId}/donations`.
 *
 * It is **declared here and parsed by the endpoint itself**, and the request
 * generator builds its sample from the same schema's inputs and checks it with
 * this very schema, so what the *API Entegrasyonu* tab shows cannot drift from
 * what the endpoint accepts. The fields are `donation.create`'s own, minus what
 * the server decides (the campaign is in the path, the channel is `api`, the
 * donation lands at the operator's institution) and plus the two that name
 * whom the integration acts as. **`.strict()`**: a key the endpoint does not
 * know is refused, not silently dropped, so a misspelt `amountMinor` is an error
 * the integrator sees rather than a donation that quietly has no amount.
 */
export const integrationCampaignDonationBodySchema = z
  .object({
    /** The idempotency key: a retry with the same value enters one donation, not two. */
    clientRequestId: z.uuid(),
    /** The staff member the donation is recorded against; the server acts as them. */
    operatorUserId: z.uuid(),
    /** Must be the operator's own institution; a mismatch is refused, never reconciled. */
    institutionId: z.uuid(),
    /** Absent resolves the campaign's active season; present is the only way into another. */
    campaignPeriodId: z.uuid().optional(),
    currency: z.enum(STAFF_ENTRY_CURRENCIES),
    quantity: z.number().int().min(1).optional(),
    amountMinor: minorAmountSchema.optional(),
    paymentMethod: donationPaymentMethodSchema.optional(),
    /** The provider payment it arrived paid by; found among the movements now or when it arrives. */
    payment: integrationPaymentSchema.optional(),
    donatedAt: z.iso.datetime({ offset: true }).optional(),
    payer: inlinePayerSchema,
    onBehalfOfName: z.string().trim().max(200).optional(),
    referenceName: z.string().trim().max(200).optional(),
    referencePhone: phoneSchema.optional(),
    formData: donationFormDataSchema.optional(),
    note: z.string().trim().max(2000).optional(),
  })
  .strict();

export type IntegrationCampaignDonationBody = z.infer<typeof integrationCampaignDonationBodySchema>;

/** The body field names, in the order a document shows them. */
export const INTEGRATION_BODY_FIELD_NAMES = Object.keys(
  integrationCampaignDonationBodySchema.shape,
) as (keyof IntegrationCampaignDonationBody)[];

export const integrationRuleSchema = z.object({
  kind: z.string(),
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
});

export type IntegrationRule = z.infer<typeof integrationRuleSchema>;

export const integrationBodyFieldSchema = z.object({
  name: z.string(),
  type: z.enum([
    'uuid',
    'enum',
    'integer',
    'minorAmount',
    'datetime',
    'string',
    'phone',
    'object',
    'record',
  ]),
  required: z.boolean(),
  rules: z.array(integrationRuleSchema),
});

export type IntegrationBodyField = z.infer<typeof integrationBodyFieldSchema>;

export const integrationFormFieldSchema = z.object({
  key: z.string(),
  type: z.string(),
  /** The field's own label — Turkish data typed by the campaign's administrator. */
  label: z.string(),
  required: z.boolean(),
  visibleOn: z
    .object({ field: z.string(), equals: z.union([z.string(), z.number(), z.boolean(), z.null()]) })
    .nullable(),
  rules: z.array(integrationRuleSchema),
  options: z.array(z.object({ value: z.string(), label: z.string() })).nullable(),
  /** False for a `file` field: a file reaches a campaign only through an authenticated upload. */
  supported: z.boolean(),
  /** Whether the generated body carries a value for it — a hidden conditional one does not. */
  inSample: z.boolean(),
});

export type IntegrationFormField = z.infer<typeof integrationFormFieldSchema>;

export const integrationHeaderSchema = z.object({
  name: z.string(),
  value: z.string(),
  /** Computed by the caller from the signing secret; never a literal secret. */
  computed: z.boolean(),
});

export const integrationResponseSchema = z.object({
  id: z.string(),
  status: z.number().int(),
  kind: z.enum(['success', 'error']),
  code: z.string().nullable(),
  reason: z.string().nullable(),
  /** The body the endpoint answers; null where it answers none at all. */
  body: z.record(z.string(), z.unknown()).nullable(),
});

export const integrationBlockerSchema = z.object({
  code: z.string(),
  reason: z.string().nullable(),
  /** For a form rejection: the answers that were refused, by field key. */
  issues: z.array(z.object({ path: z.string(), code: z.string() })),
  /**
   * The numbers and codes a Turkish refusal interpolates — a pricing refusal's
   * bound and currency. Nothing else leaves: no id, no permission name.
   */
  details: z.record(z.string(), z.union([z.string(), z.number()])),
});

export type IntegrationBlocker = z.infer<typeof integrationBlockerSchema>;

export const integrationPlaceholderSchema = z.enum(['clientRef', 'signingSecret']);

export const integrationRequestSchema = z.object({
  method: z.literal('POST'),
  /** The path with the campaign filled in and `{clientRef}` left where no client was chosen. */
  path: z.string(),
  url: z.string(),
  headers: z.array(integrationHeaderSchema),
  body: z.record(z.string(), z.unknown()),
  bodyText: z.string(),
  /** The same signed request three ways; each reads the secret from the environment and shows none. */
  examples: z.object({ sh: z.string(), php: z.string(), node: z.string() }),
});

export const integrationPreviewSchema = z.object({
  /** True only when the sample was checked against the endpoint's own rules and passed. */
  ready: z.boolean(),
  blockers: z.array(integrationBlockerSchema),
  /** What the integrator must still bring: the client's reference and its signing secret. */
  placeholders: z.array(integrationPlaceholderSchema),
  campaign: z.object({
    id: z.uuid(),
    code: z.string(),
    formVersion: z.number().int(),
    pricingMode: z.string(),
  }),
  period: z
    .object({ id: z.uuid(), seasonYear: z.number().int().nullable(), status: z.string() })
    .nullable(),
  client: z.object({ id: z.uuid(), name: z.string(), fingerprint: z.string() }).nullable(),
  request: integrationRequestSchema.nullable(),
  /** What the server priced the sample at — its own arithmetic, never the browser's. */
  quote: z.object({ amount: moneySchema, quantity: z.number().int().nullable() }).nullable(),
  bodyFields: z.array(integrationBodyFieldSchema),
  formFields: z.array(integrationFormFieldSchema),
  responses: z.array(integrationResponseSchema),
});

export type IntegrationPreview = z.infer<typeof integrationPreviewSchema>;

export const integrationInstitutionOptionSchema = z.object({ id: z.uuid(), name: z.string() });

export const integrationOperatorOptionSchema = z.object({
  id: z.uuid(),
  /** The displayed name — a pseudonym or a mask until the session reveals names. */
  name: z.string(),
  /** Whether they hold `donation.create`, which the endpoint asks of the person it acts as. */
  canEnter: z.boolean(),
});

export const campaignIntegrationContract = {
  /**
   * The institutions the caller may place a donation at — the same set the
   * entry form offers a recorder. Behind `campaign.view`, and the service also
   * asks `donation.recordForOperator`, because naming an operator is that act.
   */
  institutions: oc
    .route({ method: 'GET', path: '/campaigns/{id}/integration/institutions' })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({ id: z.uuid(), search: z.string().trim().max(120).optional() }),
    )
    .output(paginated(integrationInstitutionOptionSchema)),

  /**
   * The active users of one institution the caller may name — through the user
   * module's privilege ceiling, so a user holding a permission the caller lacks
   * is not offered. A name or an address of a user never travels unmasked.
   */
  operators: oc
    .route({ method: 'GET', path: '/campaigns/{id}/integration/operators' })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({
        id: z.uuid(),
        institutionId: z.uuid(),
        search: z.string().trim().max(120).optional(),
      }),
    )
    .output(paginated(integrationOperatorOptionSchema)),

  /**
   * The campaign's integration document: the endpoint, headers, a body the
   * server has itself checked, the cURL that signs and sends it, and the
   * rules and answers around it. **Reads only.** It enters nothing, returns no
   * secret and has no reveal; `integrationClientId` needs `integration.view`
   * and only names a client whose reference goes into the URL.
   */
  preview: oc
    .route({ method: 'GET', path: '/campaigns/{id}/integration/request' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        campaignPeriodId: z.uuid().optional(),
        institutionId: z.uuid().optional(),
        operatorUserId: z.uuid().optional(),
        integrationClientId: z.uuid().optional(),
      }),
    )
    .output(integrationPreviewSchema),
};
